import Link from 'next/link';
import { useState } from 'react';
import { ChevronDown, ChevronRight, ChevronUp, Share } from 'lucide-react';
import { displayName, extensionOf, humanSize, inlineByteSize, typeLabel } from '../../../lib/artifact-file';
import { fileTypeKind } from '../../../lib/artifact-image';
import { formatConversationDate } from '../../../lib/conversations';
import { creatorOf, folderCountLabel, folderTitle, folderWorkspaceName, pathSegments, type FileFolderRow } from '../../../lib/file-browser';
import { FILE_SORT_KEYS, nextSort, type FileSort, type FileSortKey } from '../../../lib/files-sort';
import { useTranslation } from '../../../lib/i18n';
import { FileCreatedBy } from '../FileCreatedBy';
import { FilesSpaceMark } from './FilesSpaceMark';
import { FolderGlyph } from './FolderGlyph';
import { FileActionsSheet } from './FileActionsSheet';
import { FileRowMenu } from './FileRowMenu';
import { IconButton, type OverflowMenuAction } from '../ui';
import styles from './FilesTable.module.css';
import type { FilesRow } from './files-row';

// The Files list, as rows. ONE table for both Files surfaces — the cross-workspace page and a workspace's own
// gallery — because they are the same list with a different root, and when they were two components they drifted
// (different columns, different sorting, only one had the workspace name).
//
// **Column order is Name → Built by → Workspace → Date → Type → Size**, then the trailing action slot. Every
// column except Workspace sorts (there is nothing to sort a column of badges by). A header that looks like its
// neighbours and does nothing when clicked is worse than no affordance; the rules for what sorts where, and
// where the empty values go, live in `lib/files-sort.ts`.
//
// **Workspace sits THIRD**, between "Built by" and "Date" — it used to be last, against the actions. It names
// WHO the file belongs to alongside who made it, which is the pair the reader is scanning; and since the
// breadcrumb no longer says the Space inside a merged-root folder (§13), this column is where that fact now
// lives.
//
// "Built by" and "Date" are DATA-GATED: each renders only when at least one file in scope carries that datum,
// so a list of only pre-attribution files hides the column instead of printing a wall of em-dashes.
//
// The row is a FLEX row, not a grid: secondary columns drop one at a time as the card narrows (the container
// queries in the stylesheet), and a hidden grid item would leave its track behind rather than give the name back
// the width. Below the narrow breakpoint the row becomes two stacked lines — name over meta — which is what makes
// this table work on a phone without a second mobile component to keep in step.

export function FilesTable({
  rows,
  folders,
  narrowPane,
  onOpenFolder,
  workspaceNames = {},
  showFolderWorkspace = false,
  showFileColumns = true,
  sort,
  onSortChange,
  showCreatedBy,
  showDate,
  onOpen,
  onShare,
  shareLabelKey = 'files.access.action',
  actionsFor,
  onRowMenu,
  selectedName,
}: {
  rows: FilesRow[];
  /**
   * Is the enclosing `files` size container at or under `FILES_FOLD_BREAKPOINT_PX`?
   *
   * **ONE boolean, TWO decisions on this row, and that is the whole point of it being a prop.** It
   * decides (a) whether the row renders its dedicated share glyph and (b) which presentation
   * `FileRowMenu` opens — and the SAME boolean decides, one level up in `FilesBrowser`, whether the
   * menu carries a grant-access item at all. Wide → glyph, no item. Narrow → no glyph, item, sheet.
   *
   * ⚠️ It is a PROP rather than a hook call because the question is about the enclosing container, not
   * about this component's own box or the viewport — and because whoever provides the `files` container
   * is the only one who can measure it. Both hosts do: `FilesBrowser` (`.browser`) and `FilesFlatList`
   * (`.root`). Asking it again here would be a second reader of the width, which is exactly how the
   * glyph came to be gated on the pane while the menu item was gated on the viewport, and how a
   * squeezed pane on a wide window ended up offering NEITHER.
   */
  narrowPane: boolean;
  /** The current folder's subfolders. Rendered by the TABLE, under its header — a folder is a row of this list,
   *  and rendering them above the table put the column titles halfway down the card, under the folders. */
  folders: FileFolderRow[];
  onOpenFolder: (path: string) => void;
  workspaceNames?: Record<string, string>;
  /** Name each folder's owning Space — the merged root only. */
  showFolderWorkspace?: boolean;
  /** Draw the columns only a FILE can fill (Type, Size). False when the listing has no file rows at all — the
   *  merged root of an account that keeps everything in folders is four empty columns otherwise, and a header
   *  that promises data no row on screen can supply. */
  showFileColumns?: boolean;
  sort: FileSort;
  onSortChange: (sort: FileSort) => void;
  showCreatedBy: boolean;
  showDate: boolean;
  /** Opens the file. The row is still a real link underneath (see `href`), so this is the plain-click path. */
  onOpen: (row: FilesRow) => void;
  onShare: (row: FilesRow) => void;
  /**
   * What the row's share glyph is CALLED. It opens the access dialog on the Files surfaces, so the
   * default names that — but `ChatFilesPanel` wires the same glyph to a download, and a button that
   * says "Share" and downloads is a lie to anyone reading the label rather than the icon.
   */
  shareLabelKey?: string;
  actionsFor: (row: FilesRow) => OverflowMenuAction[][];
  onRowMenu?: (row: FilesRow) => void;
  /** The file whose card is open beside the table — its row reads as selected. */
  selectedName?: string;
}) {
  const { t } = useTranslation();
  // The row whose actions SHEET is open — the phone's presentation of the row menu (`FileRowMenu`).
  // It is held HERE, beside the rows rather than inside one: a portal does not escape React's event
  // bubbling, so a scrim press inside a row's subtree would bubble to the row and open the file
  // underneath the sheet being dismissed. Same reason `FilesBrowser` holds its dialogs beside the
  // browser.
  const [sheetRow, setSheetRow] = useState<FilesRow | null>(null);

  const header = (key: FileSortKey, label: string, className: string) => (
    <span className={className}>
      <SortHeader label={label} active={sort.key === key} direction={sort.direction} onClick={() => onSortChange(nextSort(sort, key))} />
    </span>
  );

  return (
    <div className={styles.table} role="table">
      <div className={styles.headerRow} role="row">
        {header('name', t('common.name'), styles.nameCol)}
        {showCreatedBy && header('createdBy', t('files.builtBy'), `${styles.col} ${styles.colWide}`)}
        {/* THIRD, between Built by and Date. It is NAMED like the rest — an unlabelled column of badges leaves
            the reader to work out what they are — but it is not a `SortHeader`: there is nothing to sort a
            column of badges by, so it carries `.headTitle` directly and no chevron. */}
        {showFolderWorkspace && (
          <span className={`${styles.col} ${styles.space} ${styles.spaceHeader}`} role="columnheader">
            <span className={styles.headTitle}>{t('packs.section.workspace')}</span>
          </span>
        )}
        {showDate && header('date', t('agentCreations.table.date'), `${styles.col} ${styles.date}`)}
        {showFileColumns && header('type', t('agentCreations.detail.type'), `${styles.col} ${styles.type}`)}
        {showFileColumns && header('size', t('agentCreations.detail.size'), `${styles.col} ${styles.size}`)}
        <span className={styles.actions} aria-hidden />
      </div>
      {folders.map((folder) => (
        <FolderTableRow
          key={folder.path}
          folder={folder}
          workspaceNames={workspaceNames}
          showWorkspace={showFolderWorkspace}
          showCreatedBy={showCreatedBy}
          showDate={showDate}
          showFileColumns={showFileColumns}
          onOpen={onOpenFolder}
        />
      ))}

      {rows.map((row) => {
        const size = inlineByteSize(row.item);
        const type = row.item.isApp ? null : typeLabel(extensionOf(row.item));
        const creator = creatorOf(row.item);
        const selected = selectedName != null && row.item.name === selectedName;
        return (
          <Link
            key={row.key}
            href={row.href}
            as={row.as}
            className={styles.row}
            role="row"
            data-selected={selected || undefined}
            /* A click on the row opens the file, right here — the thing the row is obviously for. It stays a
               real link underneath, so cmd/middle-click and "open in new tab" keep working and the status bar
               shows a destination; only the plain left-click is intercepted. */
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
              e.preventDefault();
              onOpen(row);
            }}
          >
            <span className={styles.name}>
              <span className={styles.icon}>
                {/* 24 tall inside the 32px chip on the desktop row, 28 inside the phone row's 36px tile
                    (Figma). The two sizes are a container query in the stylesheet, so these attributes
                    are only the pre-CSS intrinsic size — a presentation attribute has no specificity,
                    which is why `.icon > img` can restate it per width. */}
                <img
                  className={styles.fileGlyph}
                  src={`${row.basePath}/file-types/${fileTypeKind(extensionOf(row.item))}.png`}
                  alt=""
                  width={19}
                  height={24}
                />
              </span>
              <span className={styles.nameStack}>
                <span className={styles.nameText}>{displayName(row.item)}</span>
                {/* WHERE the file lives. With folders in the picture the workspace alone stops being an answer
                    once two folders hold a file of the same name. */}
                {row.caption && <span className={styles.caption}>{row.caption}</span>}
                {/* WHO built it, under the name, for the width that has no columns left. Hidden by the
                    stylesheet at every width that still has a Built-by column, so nothing is said twice.

                    It carries the AGENT TAG only — a 12px mark and the writer's name. The phone frame
                    (Figma `15891:420486`) shows no date, no type and no size on a row: what a row is for
                    there is the name, who made it, which Space, and the way in. Those facts have not gone
                    anywhere; they are on the file's own screen, which the row opens. */}
                <span className={styles.metaLine}>{creator && <FileCreatedBy creator={creator} size={12} />}</span>
              </span>
            </span>
            {showCreatedBy && (
              <span className={`${styles.col} ${styles.colWide} ${styles.createdBy}`}>
                {creator ? <FileCreatedBy creator={creator} size={15} /> : <span className={styles.muted}>—</span>}
              </span>
            )}
            {/* Third, between Built by and Date — and on the SAME flex share as every other secondary column,
                so the badge lands at one x down the whole list without a shared grid template. */}
            {showFolderWorkspace && (
              <span className={`${styles.col} ${styles.space}`}>
                <SpaceCell id={row.item.workspaceId} name={row.workspaceName} />
              </span>
            )}
            {showDate && (
              <span className={`${styles.col} ${styles.date} ${styles.muted}`}>
                {row.item.updatedDate ? formatConversationDate(row.item.updatedDate) : '—'}
              </span>
            )}
            {showFileColumns && <span className={`${styles.col} ${styles.type} ${styles.muted}`}>{type ?? '—'}</span>}
            {showFileColumns && (
              <span className={`${styles.col} ${styles.size} ${styles.muted}`}>{size != null ? humanSize(size) : '—'}</span>
            )}
            <span className={styles.actions}>
              {/* Share sits OUTSIDE the overflow: it is the action a file row is most often opened for, and a
                  glyph beside the dots costs nothing. Everything else stays in the menu.

                  ⚠️ ON A WIDE PANE ONLY, and this `!narrowPane` is HALF OF ONE CONDITION — the other half
                  is `FilesBrowser` handing `onGrantAccess` to the menu builder when `narrowPane`. The two
                  read the same boolean, so the pair can never both be absent. It used to be a
                  `display: none` inside `@container files (max-width: 600px)` while the menu item was
                  gated on `useNarrow()` (the VIEWPORT, 720): a wide window with a docked chat squeezed
                  the pane under 600, the CSS hid this button, the viewport said "not narrow" so the menu
                  grew no item, and there was no way to grant access at all. Gating it here, on the
                  browser's own measurement of the container the query asks about, is what makes that
                  state unreachable rather than merely unlikely.

                  Not-rendered rather than `display: none`, so there is one mechanism instead of two: a
                  stylesheet that can also hide it is a second gate, and a second gate is the bug. */}
              {/* lucide `Share` — the tray with the arrow leaving it — NOT `Share2`, the three-node graph.
                  Same 16px, same action, same label; the redesign settles on one share mark across web,
                  iOS (`Icon.shareUpload`) and Android (`OIcon.shareUpload`). */}
              {!narrowPane && (
                <IconButton
                  size="tiny"
                  aria-label={t(shareLabelKey)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onShare(row);
                  }}
                >
                  <Share size={16} aria-hidden />
                </IconButton>
              )}
              {/* Wide → the compact popover; narrow → the bottom sheet below. The SAME boolean that
                  decided the glyph above, so the presentation and the actions it carries agree by
                  construction — see `FileRowMenu`. */}
              <FileRowMenu
                narrowPane={narrowPane}
                groups={actionsFor(row)}
                ariaLabel={t('agentCreations.table.options')}
                onOpen={() => onRowMenu?.(row)}
                onSheet={() => setSheetRow(row)}
              />
            </span>
          </Link>
        );
      })}

      {/* ONE sheet for the whole list, outside every row — see `sheetRow` above. */}
      {sheetRow && (
        <FileActionsSheet
          open
          onClose={() => setSheetRow(null)}
          title={displayName(sheetRow.item)}
          groups={actionsFor(sheetRow)}
        />
      )}
    </div>
  );
}

/**
 * The row's Space badge — rendered TWICE, and the stylesheet shows exactly one.
 *
 * The two frames give the mark different sizes: 16 in the desktop list's Workspace column
 * (Figma `15623:362461`), 20 in the phone row's trailing group (`15891:420486`). Which one applies is
 * decided by the CONTAINER's width, and a React prop cannot read a container query — while
 * `WorkspaceBadge` sizes itself from an inline `width`/`height`, which no stylesheet can outrank.
 * So both are in the DOM and `display: none` picks. That also keeps the accessible name announced
 * once: `display: none` removes the hidden twin from the accessibility tree entirely.
 */
function SpaceCell({ id, name }: { id?: string; name?: string }) {
  return (
    <>
      <span className={styles.spaceWide}>
        <FilesSpaceMark id={id} name={name} size={16} />
      </span>
      <span className={styles.spaceNarrow}>
        <FilesSpaceMark id={id} name={name} size={20} />
      </span>
    </>
  );
}

/** A sortable column header. Not a DS Button: it is a table header that carries a direction glyph, and the
 *  button skins would put a box around every column title. */
function SortHeader({
  label,
  active,
  direction,
  onClick,
}: {
  label: string;
  active: boolean;
  direction: FileSort['direction'];
  onClick: () => void;
}) {
  return (
    <span
      className={styles.sortHeader}
      role="columnheader"
      tabIndex={0}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <span className={styles.headTitle}>{label}</span>
      {active && (direction === 'asc' ? <ChevronUp size={16} aria-hidden /> : <ChevronDown size={16} aria-hidden />)}
    </span>
  );
}

/** Exported for the guard test: every sort key has a column, and every column sorts. */
export const SORTABLE_COLUMNS = FILE_SORT_KEYS;

/**
 * A folder, as a row of the SAME table the files are in: it takes the file rows' column skeleton — the Space
 * column, the data columns (empty; a folder has no writer or date of its own), the word "Folder" under Type, an
 * em-dash under Size, and the trailing actions slot holding a chevron instead of the two buttons.
 *
 * It borrows that skeleton rather than laying itself out because a folder row and a file row are read as one
 * list: with its own layout, the Space badge landed at a different x on each kind and the right edges did not
 * line up, which is what made the list look ragged.
 */
function FolderTableRow({
  folder,
  workspaceNames,
  showWorkspace,
  showCreatedBy,
  showDate,
  showFileColumns,
  onOpen,
}: {
  folder: FileFolderRow;
  workspaceNames: Record<string, string>;
  showWorkspace: boolean;
  showCreatedBy: boolean;
  showDate: boolean;
  showFileColumns: boolean;
  onOpen: (path: string) => void;
}) {
  const { t } = useTranslation();
  const segments = pathSegments(folder.path);
  const title = folderTitle(folder.path, workspaceNames, t);
  const space = showWorkspace ? folderWorkspaceName(folder.path, workspaceNames) : '';
  const count = folderCountLabel(folder, t);

  return (
    <div
      className={styles.row}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(folder.path)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(folder.path);
        }
      }}
    >
      {/* The row IS a file row's skeleton with a folder's content: the same name column, the same data cells
          (empty — a folder has no writer or date), "Folder" where a file names its type, an em-dash where it
          states its size, and a chevron in the actions slot. The empty cells carry the file cells' OWN classes,
          not a bespoke placeholder, so the container queries that drop Size, then Type, then Date hide them on
          exactly the same widths; a placeholder of its own stayed visible after its file-row twin had gone and
          pushed the trailing cells out of line. */}
      <span className={styles.name}>
        {/* The two-tone folder, painted in the owning Space's own avatar tones — see `FolderGlyph`. It
            replaced the lucide outline `Folder`, which looked the same for every Space. 18×14 inside the
            32px chip, which is a step darker than a file's so a folder reads as a folder at a glance. */}
        <span className={`${styles.icon} ${styles.iconFolder}`}>
          <FolderGlyph workspaceId={segments[0]} width={17.797} />
        </span>
        <span className={`${styles.nameStack} ${styles.nameStackFolder}`}>
          <span className={styles.nameText}>{title}</span>
          {/* WHAT IS IN IT, under the name — the phone row's second line (Figma `15891:420486`), where
              there is no Size column for the glyph pair below to live in. The same sentence
              `folderCountLabel` already builds for that column's accessible name, so the two can never
              disagree. Hidden by the stylesheet at every width that still draws the column. */}
          <span className={styles.folderMeta}>{count}</span>
        </span>
      </span>
      {showCreatedBy && <span className={`${styles.col} ${styles.colWide} ${styles.createdBy}`} />}
      {showWorkspace && (
        <span className={`${styles.col} ${styles.space}`}>
          <SpaceCell id={segments[0]} name={space} />
        </span>
      )}
      {showDate && <span className={`${styles.col} ${styles.date}`} />}
      {/* WHAT THIS ROW IS, in the column where a file names its format. A folder has no extension to read, and
          "Folder" is the one word that tells the reader why the rest of the row is blank — without it, a folder
          is a row whose last two columns are simply missing. */}
      {showFileColumns && <span className={`${styles.col} ${styles.type} ${styles.muted}`}>{t('files.type.folder')}</span>}
      {/* NOTHING, in the column where a file states its size — the same em-dash a file row prints when its own
          size is unknown, because a folder has no size of its own to give. It used to hold the counts as a pair
          of glyph+number lines ("🗀 5 / 🗋 2"); two icon-and-number stacks in a numeric column read as a second
          kind of data rather than as an empty cell, and the count is still one line under the name at the widths
          that have no Size column at all (`.folderMeta`). It is a real column either way: its flex share is what
          puts the Space at the same x on both kinds of row. */}
      {showFileColumns && <span className={`${styles.col} ${styles.size} ${styles.muted}`}>—</span>}
      <span className={styles.actions}>
        {/* In a box the size of the file rows' overflow button, so the chevron's centre lands on the ⋮'s
            rather than merely sharing its right edge — the glyphs are 16 and 30 wide. */}
        <span className={styles.chevronSlot}>
          <ChevronRight className={`${styles.chevron} dir-flip`} size={16} aria-hidden />
        </span>
      </span>
    </div>
  );
}
